import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
export interface SplitPart {
  value: number;
  label?: string;
}
export interface SplitDiagramProps {
  /** The original number being split */
  original: number;
  /** The parts the number is split into */
  parts: SplitPart[];
  /** The operation context */
  operation?: 'add' | 'subtract' | 'multiply';
  /** The other operand (e.g., 48 in "48 + 37") */
  operand?: number;
  /** Final result */
  result?: number;
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
const PART_COLORS = [{
  bg: 'bg-blue-100',
  border: 'border-blue-300',
  text: 'text-blue-800',
  dot: 'bg-blue-500'
}, {
  bg: 'bg-emerald-100',
  border: 'border-emerald-300',
  text: 'text-emerald-800',
  dot: 'bg-emerald-500'
}, {
  bg: 'bg-amber-100',
  border: 'border-amber-300',
  text: 'text-amber-800',
  dot: 'bg-amber-500'
}, {
  bg: 'bg-purple-100',
  border: 'border-purple-300',
  text: 'text-purple-800',
  dot: 'bg-purple-500'
}];
const OP_SYMBOL: Record<string, string> = {
  add: '+',
  subtract: '−',
  multiply: '×'
};
function formatNL(n: number): string {
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0
  }).format(n);
}
function SplitBranch({
  part,
  colorIdx,
  delay,
  isLast
}: {
  part: SplitPart;
  colorIdx: number;
  delay: number;
  isLast: boolean;
}) {
  const c = PART_COLORS[colorIdx % PART_COLORS.length];
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.3,
    ease: 'easeOut'
  }} className="flex items-center gap-2" data-id="element-12944">
      {/* Branch line */}
      <div className="flex flex-col items-center w-6" data-id="element-12945">
        <div className={`w-3 h-3 rounded-full ${c.dot}`} data-id="element-12946" />
        {!isLast && <div className="w-px h-4 bg-slate-300" data-id="element-12947" />}
      </div>
      {/* Value chip */}
      <div className={`${c.bg} ${c.border} border rounded-lg px-3 py-1.5 ${c.text} font-bold text-sm`} data-id="element-12948">
        {formatNL(part.value)}
      </div>
    </motion.div>;
}
function StepArrow({
  from,
  op,
  partValue,
  to,
  delay,
  colorIdx
}: {
  from: number;
  op: string;
  partValue: number;
  to: number;
  delay: number;
  colorIdx: number;
}) {
  const c = PART_COLORS[colorIdx % PART_COLORS.length];
  return <motion.div initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay,
    duration: 0.3,
    ease: 'easeOut'
  }} className="flex items-center gap-2 text-sm" data-id="element-12949">
      <span className="font-mono text-slate-600 w-16 text-right" data-id="element-12950">
        {formatNL(from)}
      </span>
      <span className={`font-bold ${c.text}`} data-id="element-12951">
        {op} {formatNL(partValue)}
      </span>
      <svg className="w-4 h-4 text-slate-400 flex-shrink-0" viewBox="0 0 16 16" fill="none" data-id="element-12952">
        <path d="M3 8h10M10 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12953" />
      </svg>
      <span className="font-bold text-slate-800" data-id="element-12954">{formatNL(to)}</span>
    </motion.div>;
}
export function SplitDiagram({
  original,
  parts,
  operation = 'add',
  operand,
  result,
  label,
  mode = 'support'
}: SplitDiagramProps) {
  if (!parts || parts.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-12955">
        <p className="text-red-600 text-sm font-medium" data-id="element-12956">Ongeldige splitsing</p>
      </div>;
  }
  const op = OP_SYMBOL[operation] ?? '+';
  // Calculate intermediate results for the step-by-step view
  const steps: {
    from: number;
    partValue: number;
    to: number;
  }[] = [];
  if (operand !== undefined) {
    let current = operand;
    for (const part of parts) {
      const next = operation === 'subtract' ? current - part.value : operation === 'multiply' ? current * part.value : current + part.value;
      steps.push({
        from: current,
        partValue: part.value,
        to: next
      });
      current = next;
    }
  }
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-indigo-50 rounded-xl px-5 py-4 border border-indigo-200" aria-label={label ?? `Splitsstrategie: ${formatNL(original)}`} data-id="element-12957">
        {label && <p className="text-sm font-semibold text-indigo-800 mb-3 text-center" data-id="element-12958">
            {label}
          </p>}
        <div className="flex items-center justify-center gap-3 flex-wrap" data-id="element-12959">
          {/* Original number */}
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="bg-indigo-200 border border-indigo-400 rounded-lg px-4 py-2 font-bold text-indigo-900 text-lg" data-id="element-12960">
            {formatNL(original)}
          </motion.div>
          {/* Arrow */}
          <svg className="w-5 h-5 text-indigo-400 flex-shrink-0" viewBox="0 0 16 16" fill="none" data-id="element-12961">
            <path d="M3 8h10M10 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12962" />
          </svg>
          {/* Parts */}
          <div className="flex items-center gap-1.5" data-id="element-12963">
            {parts.map((part, i) => {
            const c = PART_COLORS[i % PART_COLORS.length];
            return <Fragment key={i} data-id="element-12964">
                  {i > 0 && <span className="text-slate-400 font-bold text-sm" data-id="element-12965">+</span>}
                  <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 0.15 + i * 0.1,
                duration: 0.3
              }} className={`${c.bg} ${c.border} border rounded-lg px-3 py-1.5 font-bold ${c.text} text-sm`} data-id="element-12966">
                    {formatNL(part.value)}
                  </motion.div>
                </Fragment>;
          })}
          </div>
        </div>
      </div>;
  }
  // Support mode: educational panel with step-by-step
  return <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-2xl border-2 border-indigo-200 shadow-sm" aria-label={label ?? `Splitsstrategie: ${formatNL(original)}`} data-id="element-12967">
      {label && <div className="mb-5 text-center" data-id="element-12968">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-indigo-200 shadow-sm" data-id="element-12969">
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" data-id="element-12970" />
            <span className="text-sm font-bold text-indigo-900" data-id="element-12971">{label}</span>
          </div>
        </div>}

      {/* Split visualization */}
      <div className="bg-white/80 rounded-xl p-4 border border-indigo-100 mb-4" data-id="element-12972">
        <div className="flex items-start gap-4" data-id="element-12973">
          {/* Original number */}
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="bg-indigo-200 border-2 border-indigo-400 rounded-xl px-5 py-3 font-bold text-indigo-900 text-xl flex-shrink-0" data-id="element-12974">
            {formatNL(original)}
          </motion.div>

          {/* Branch connector */}
          <div className="flex flex-col items-center pt-2" data-id="element-12975">
            <svg className="w-6 h-6 text-slate-400" viewBox="0 0 24 24" fill="none" data-id="element-12976">
              <path d="M5 12h14M14 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12977" />
            </svg>
          </div>

          {/* Parts */}
          <div className="space-y-1" data-id="element-12978">
            {parts.map((part, i) => <SplitBranch key={i} part={part} colorIdx={i} delay={0.2 + i * 0.12} isLast={i === parts.length - 1} data-id="element-12979" />)}
          </div>
        </div>
      </div>

      {/* Step-by-step calculation (only when operand is provided) */}
      {steps.length > 0 && <div className="bg-white/80 rounded-xl p-4 border border-indigo-100 mb-4" data-id="element-12980">
          <h4 className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-3" data-id="element-12981">
            Stap voor stap
          </h4>
          <div className="space-y-2" data-id="element-12982">
            {steps.map((step, i) => <StepArrow key={i} from={step.from} op={op} partValue={step.partValue} to={step.to} delay={0.4 + i * 0.15} colorIdx={i} data-id="element-12983" />)}
          </div>
          {result !== undefined && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4 + steps.length * 0.15
      }} className="mt-3 pt-3 border-t border-indigo-100 text-center" data-id="element-12984">
              <span className="text-sm text-slate-600" data-id="element-12985">Uitkomst: </span>
              <span className="font-bold text-lg text-indigo-900" data-id="element-12986">
                {formatNL(result)}
              </span>
            </motion.div>}
        </div>}

      {/* Educational explanation */}
      <div className="bg-white/80 rounded-xl p-4 border border-indigo-100 shadow-sm" data-id="element-12987">
        <div className="flex items-start gap-3" data-id="element-12988">
          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-12989">
            <span className="text-indigo-600 text-sm" data-id="element-12990">✂️</span>
          </div>
          <div data-id="element-12991">
            <h4 className="text-sm font-bold text-indigo-900 mb-1" data-id="element-12992">
              Splitsstrategie
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12993">
              Bij splitsen verdeel je <strong data-id="element-12994">{formatNL(original)}</strong> in
              handige stukken:{' '}
              {parts.map((p, i) => <Fragment key={i} data-id="element-12995">
                  {i > 0 && (i === parts.length - 1 ? ' en ' : ', ')}
                  <strong data-id="element-12996">{formatNL(p.value)}</strong>
                </Fragment>)}
              . Zo kun je stap voor stap rekenen.
            </p>
          </div>
        </div>
      </div>
    </div>;
}